import { inject, injectable } from 'tsyringe';
import { AuthFetchToken, type AuthFetch } from '@/container/IntegrationTokens';

export interface OrgProfileFields {
    description: string;
    website: string;
    industry: string | null;
    region: string | null;
    size: string | null;
}

export interface OrganizationSettings {
    name: string;
    memberCount: number;
    profile: OrgProfileFields;
    /** BFF URL to fetch the stored logo, cache-busted by the tenant's updatedAt; null when unset. */
    logoUrl: string | null;
}

/**
 * Logo patch is tri-state: a `data:` URL uploads a new image, `null` removes it,
 * and `undefined` leaves the stored logo untouched.
 */
export type LogoPatch = string | null | undefined;

export const EMPTY_ORG_PROFILE_FIELDS: OrgProfileFields = {
    description: '',
    website: '',
    industry: null,
    region: null,
    size: null,
};

/** BFF proxy prefix for auth-api (stripped by the upstream router → :3001). */
const AUTH_BASE_PATH = '/_auth';
const CURRENT_TENANT_URL = `${AUTH_BASE_PATH}/api/tenants/current`;
const LOGO_URL = `${CURRENT_TENANT_URL}/logo`;

/**
 * Reads and writes the current tenant's organization profile via auth-api.
 * The company name maps to the tenant's `tenantName`; the text profile lives in
 * the tenant's `config.profile` jsonb; the logo bytes live in blob storage and
 * are served from {@link LOGO_URL}.
 */
@injectable()
export class OrganizationSettingsService {
    constructor(@inject(AuthFetchToken) private readonly authFetch: AuthFetch) {}

    async load(): Promise<OrganizationSettings> {
        const response = await this.authFetch(CURRENT_TENANT_URL);
        if (!response.ok) {
            throw new Error(`Failed to load organization (${response.status} ${response.statusText})`);
        }
        return toSettings(await response.json());
    }

    async save(input: { name: string; profile: OrgProfileFields; logo: LogoPatch }): Promise<OrganizationSettings> {
        const profile: Record<string, unknown> = { ...input.profile };
        if (input.logo !== undefined) profile['logoDataUrl'] = input.logo;

        const response = await this.authFetch(CURRENT_TENANT_URL, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tenantName: input.name, profile }),
        });
        if (!response.ok) {
            throw new Error(`Failed to save organization (${response.status} ${response.statusText})`);
        }
        return toSettings(await response.json());
    }
}

function toSettings(body: unknown): OrganizationSettings {
    const raw = (body ?? {}) as Record<string, unknown>;
    const profileRaw = (raw['profile'] ?? {}) as Record<string, unknown>;
    const hasLogo = profileRaw['logo'] !== null && typeof profileRaw['logo'] === 'object';
    const version = typeof raw['updatedAt'] === 'string' ? raw['updatedAt'] : '';
    return {
        name: typeof raw['name'] === 'string' ? raw['name'] : '',
        memberCount: typeof raw['memberCount'] === 'number' ? raw['memberCount'] : 0,
        profile: toFields(profileRaw),
        logoUrl: hasLogo ? `${LOGO_URL}?v=${encodeURIComponent(version)}` : null,
    };
}

function toFields(raw: Record<string, unknown>): OrgProfileFields {
    const str = (v: unknown): string => (typeof v === 'string' ? v : '');
    const nullable = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
    return {
        description: str(raw['description']),
        website: str(raw['website']),
        industry: nullable(raw['industry']),
        region: nullable(raw['region']),
        size: nullable(raw['size']),
    };
}
